// Viewport-based marker management for Google Maps
// Efficiently handles large numbers of markers by only rendering visible ones
// Follows best practices to prevent memory leaks and CPU heating issues

import { Shop } from '../types/Shop';

export interface ViewportBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

export class ViewportMarkerManager {
    private map: google.maps.Map;
    private allShops: Shop[] = [];
    private bufferPercent = 0.25; // 25% buffer around viewport

    // Memory leak prevention: Store all references for proper cleanup
    private updateTimeout: number | null = null;
    private boundsListener: google.maps.MapsEventListener | null = null;
    private zoomListener: google.maps.MapsEventListener | null = null;
    private isDestroyed = false;
    private isUpdating = false;

    constructor(map: google.maps.Map) {
        this.map = map;
        this.setupEventListeners();
    }

    private setupEventListeners() {
        // CPU optimization: Throttle updates to prevent excessive calls during pan/zoom
        const throttledUpdate = () => {
            // Prevent memory leaks: Clear existing timeout
            if (this.updateTimeout) {
                clearTimeout(this.updateTimeout);
                this.updateTimeout = null;
            }

            // Prevent updates after destruction
            if (this.isDestroyed) return;

            this.updateTimeout = setTimeout(() => {
                this.notifyViewportChange();
                this.updateTimeout = null;
            }, 200); // 200ms throttle - balance between responsiveness and CPU usage
        };

        // Store listener references for proper cleanup (prevents memory leaks)
        this.boundsListener = this.map.addListener('bounds_changed', throttledUpdate);
        this.zoomListener = this.map.addListener('zoom_changed', throttledUpdate);

        console.log('🗺️ [ViewportManager] Event listeners setup complete');
    }

    // Get current viewport bounds with buffer
    getViewportBounds(): ViewportBounds | null {
        if (this.isDestroyed) return null;

        const bounds = this.map.getBounds();
        if (!bounds) return null;

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        const latDiff = ne.lat() - sw.lat();
        const lngDiff = ne.lng() - sw.lng();

        return {
            north: ne.lat() + (latDiff * this.bufferPercent),
            south: sw.lat() - (latDiff * this.bufferPercent),
            east: ne.lng() + (lngDiff * this.bufferPercent),
            west: sw.lng() - (lngDiff * this.bufferPercent)
        };
    }

    // Check if a shop is within the viewport bounds
    isShopInViewport(shop: Shop, bounds?: ViewportBounds): boolean {
        const viewportBounds = bounds || this.getViewportBounds();
        if (!viewportBounds) return true; // Show all if can't determine bounds

        const [lat, lng] = shop.coordinates;

        // Handle longitude wraparound (crossing 180/-180 meridian)
        let inLngRange: boolean;
        if (viewportBounds.west <= viewportBounds.east) {
            // Normal case
            inLngRange = lng >= viewportBounds.west && lng <= viewportBounds.east;
        } else {
            // Wraparound case (e.g., viewing across Pacific)
            inLngRange = lng >= viewportBounds.west || lng <= viewportBounds.east;
        }

        return lat >= viewportBounds.south && lat <= viewportBounds.north && inLngRange;
    }

    // Get shops that are currently visible in viewport
    getVisibleShops(): Shop[] {
        if (this.isDestroyed) return [];

        const bounds = this.getViewportBounds();
        if (!bounds) return this.allShops; // Return all if can't determine bounds

        return this.allShops.filter(shop => this.isShopInViewport(shop, bounds));
    }

    // Update the shop data
    updateShops(shops: Shop[]) {
        if (this.isDestroyed) return;

        this.allShops = shops;

        console.log('🏪 [ViewportManager] Updated shops data:', {
            totalShops: shops.length,
            viewportShops: this.getVisibleShops().length
        });
    }

    // Notify about viewport changes (for external components to react)
    private notifyViewportChange() {
        if (this.isDestroyed || this.isUpdating) return;

        this.isUpdating = true;

        try {
            const visibleShops = this.getVisibleShops();
            const bounds = this.getViewportBounds();

            console.log('👁️ [ViewportManager] Viewport changed:', {
                totalShops: this.allShops.length,
                visibleShops: visibleShops.length,
                bounds: bounds ? {
                    north: bounds.north.toFixed(4),
                    south: bounds.south.toFixed(4),
                    east: bounds.east.toFixed(4),
                    west: bounds.west.toFixed(4)
                } : null
            });

            // Dispatch custom event for components to listen to
            const event = new CustomEvent('viewportShopsChanged', {
                detail: { visibleShops, bounds }
            });
            window.dispatchEvent(event);

        } catch (error) {
            console.error('❌ [ViewportManager] Error in viewport change:', error);
        } finally {
            this.isUpdating = false;
        }
    }

    // Get statistics for debugging
    getStats() {
        const bounds = this.getViewportBounds();
        const visibleShops = this.getVisibleShops();

        return {
            totalShops: this.allShops.length,
            visibleShops: visibleShops.length,
            currentBounds: bounds,
            bufferPercent: this.bufferPercent,
            isDestroyed: this.isDestroyed
        };
    }

    // Set buffer percentage (how much extra area around viewport to load)
    setBufferPercent(percent: number) {
        if (this.isDestroyed) return;

        this.bufferPercent = Math.max(0, Math.min(1, percent)); // Clamp between 0-100%
        this.notifyViewportChange();
    }

    // CRITICAL: Clean up all event listeners and references to prevent memory leaks
    destroy() {
        if (this.isDestroyed) return;

        console.log('🗑️ [ViewportManager] Cleaning up viewport manager');
        this.isDestroyed = true;

        // Clear any pending timeouts (prevents memory leaks)
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = null;
        }

        // Remove Google Maps event listeners (prevents memory leaks)
        if (this.boundsListener) {
            google.maps.event.removeListener(this.boundsListener);
            this.boundsListener = null;
        }
        if (this.zoomListener) {
            google.maps.event.removeListener(this.zoomListener);
            this.zoomListener = null;
        }

        // Clear all references
        this.allShops = [];

        console.log('✅ [ViewportManager] Cleanup complete');
    }
}